import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  extractYouTubeVideoId,
  extractYouTubeAudioId,
} from "../utils/validation.jsx";

const ContentDisplay = ({
  contents,
  settings,
  music,
  userInteracted,
  onCycleComplete,
  cycleCount,
  sessionAuthenticated,
}) => {
  const [currentContentIndex, setCurrentContentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [currentCycleTime, setCurrentCycleTime] = useState(0);

  // 전환 효과를 위한 상태들
  const [nextContentIndex, setNextContentIndex] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [renderElements, setRenderElements] = useState([]);

  // 타이머 및 오디오 관리용 Ref
  const initialLoadHandledRef = useRef(false);
  const contentTimerRef = useRef(null);
  const backgroundMusicRef = useRef(null);
  const cycleTimerRef = useRef(null);
  const musicStartTimeRef = useRef(0);

  // 배경음악 중복 초기화 방지용 Ref
  const musicInitializingRef = useRef(false);
  const lastMusicDataRef = useRef(null);

  // 배경음악 상태 (기존 로직 완전 유지)
  const [backgroundMusicPlayer, setBackgroundMusicPlayer] = useState(null);
  const [backgroundYouTubePlayer, setBackgroundYouTubePlayer] = useState(null);
  const [isMusicInitialized, setIsMusicInitialized] = useState(false);
  const [musicCurrentTime, setMusicCurrentTime] = useState(0);
  const [isYouTubeAPILoaded, setIsYouTubeAPILoaded] = useState(false);

  // YouTube 컨텐츠 플레이어 상태 추가 (기존 로직에 추가)
  const [contentYouTubePlayer, setContentYouTubePlayer] = useState(null);

  // YouTube API 관리
  const youtubeCallbacksRef = useRef([]);

  // 현재 컨텐츠 정보
  const currentContent = contents[currentContentIndex];
  const transitionDuration = settings?.transitionDuration || 1;
  const transitionStyle = settings?.transitionStyle || "fade";

  // 전체 컨텐츠 재생 시간 계산
  const totalContentDuration = contents.reduce((total, content) => {
    return total + (content.playDuration || 10);
  }, 0);

  // 배경음악 설정 시간 (첫 번째 음악 기준)
  const backgroundMusicDuration =
    music && music.length > 0 ? music[0].playDuration || 180 : 0;

  // 배경음악이 활성화되어 있는지 확인
  const isBgmEnabled = settings?.isBgmEnabled && music && music.length > 0;

  // YouTube API 로드 함수 (기존 로직 완전 유지)
  const loadYouTubeAPI = useCallback((callback) => {
    if (window.YT && window.YT.Player) {
      callback();
      return;
    }

    youtubeCallbacksRef.current.push(callback);

    if (document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      return;
    }

    const script = document.createElement("script");
    script.src = "https://www.youtube.com/iframe_api";

    window.onYouTubeIframeAPIReady = () => {
      setIsYouTubeAPILoaded(true);
      youtubeCallbacksRef.current.forEach((cb) => {
        try {
          cb();
        } catch (error) {
          // Silent error handling
        }
      });
      youtubeCallbacksRef.current = [];
    };

    document.head.appendChild(script);
  }, []);

  // 기존 배경음악 완전 정리 함수 (중복 재생 방지)
  const cleanupBackgroundMusic = useCallback(() => {
    // 일반 Audio 플레이어 완전 정리
    if (backgroundMusicRef.current) {
      try {
        backgroundMusicRef.current.pause();
        backgroundMusicRef.current.removeEventListener("loadeddata", () => {});
        backgroundMusicRef.current.removeEventListener("timeupdate", () => {});
        backgroundMusicRef.current.removeEventListener("ended", () => {});
        backgroundMusicRef.current.removeEventListener("error", () => {});
        backgroundMusicRef.current.src = "";
        backgroundMusicRef.current.load(); // Audio 객체 완전 초기화
        backgroundMusicRef.current = null;
      } catch (error) {
        // Silent error handling
      }
    }

    if (backgroundMusicPlayer) {
      try {
        backgroundMusicPlayer.pause();
        backgroundMusicPlayer.src = "";
        backgroundMusicPlayer.load(); // Audio 객체 완전 초기화
      } catch (error) {
        // Silent error handling
      }
      setBackgroundMusicPlayer(null);
    }

    // YouTube 배경음악 플레이어 완전 정리
    if (backgroundYouTubePlayer) {
      try {
        backgroundYouTubePlayer.pauseVideo();
        backgroundYouTubePlayer.stopVideo();
        backgroundYouTubePlayer.destroy();
      } catch (error) {
        // Silent error handling
      }
      setBackgroundYouTubePlayer(null);
    }

    // YouTube 배경음악 컨테이너 정리
    const bgContainer = document.getElementById("backgroundYouTubeContainer");
    if (bgContainer) {
      bgContainer.innerHTML = "";
      bgContainer.remove();
    }

    setIsMusicInitialized(false);
  }, [backgroundMusicPlayer, backgroundYouTubePlayer]);

  // YouTube 배경음악 플레이어 생성 (원래 로직 완전 유지 + getIframe 제어 추가)
  const createBackgroundYouTubePlayer = useCallback(
    (videoId, volume) => {
      // 기존 컨테이너 완전 정리
      let container = document.getElementById("backgroundYouTubeContainer");
      if (container) {
        container.innerHTML = "";
        container.remove();
      }

      // 새 컨테이너 생성 (원래 위치: 화면 밖 숨겨진 위치)
      container = document.createElement("div");
      container.id = "backgroundYouTubeContainer";
      container.style.cssText =
        "position: absolute; top: -1000px; left: -1000px; opacity: 0; pointer-events: none;";
      document.body.appendChild(container);

      // 고유한 플레이어 ID 생성
      const playerId = `background-youtube-player-${Date.now()}`;
      container.innerHTML = `<div id="${playerId}"></div>`;

      try {
        const player = new window.YT.Player(playerId, {
          height: "0",
          width: "0",
          videoId: videoId,
          playerVars: {
            autoplay: 1,
            loop: 0, // 수동으로 반복 관리
            controls: 0,
            disablekb: 1,
            fs: 0,
            modestbranding: 1,
            rel: 0,
            showinfo: 0,
            mute: 0,
            start: 0,
          },
          events: {
            onReady: (event) => {
              try {
                event.target.setVolume(volume * 100);
                if (isPlaying) {
                  event.target.playVideo();
                }
                setIsMusicInitialized(true);
              } catch (error) {
                // Silent error handling
              }
            },
            onStateChange: (event) => {
              if (event.data === window.YT.PlayerState.PLAYING) {
                // 재생 시작
              } else if (event.data === window.YT.PlayerState.ENDED) {
                // 재생 완료 시 반복 로직 처리
                handleYouTubeMusicEnd(event.target);
              } else if (event.data === window.YT.PlayerState.PAUSED) {
                // 일시정지
              }
            },
            onError: (event) => {
              // Silent error handling
            },
          },
        });

        setBackgroundYouTubePlayer(player);
      } catch (error) {
        // Silent error handling
      }
    },
    [isPlaying]
  );

  // YouTube 컨텐츠 플레이어 생성 함수 (화면에 보이는 위치) - TV 자동재생 강화
  const createContentYouTubePlayer = useCallback(
    (videoId, isMuted = true) => {
      // 기존 플레이어 정리
      if (contentYouTubePlayer) {
        try {
          contentYouTubePlayer.destroy();
        } catch (error) {
          // Silent error handling
        }
      }

      // 기존 컨테이너 완전 정리
      let container = document.getElementById("contentYouTubeContainer");
      if (container) {
        container.innerHTML = "";
        container.remove();
      }

      // 컨테이너 생성 (화면에 보이는 위치)
      const playerId = `content-youtube-player-${Date.now()}`;
      container = document.createElement("div");
      container.id = "contentYouTubeContainer";
      container.style.cssText =
        "position: fixed; top: 0; left: 0; width: 100%; height: 100%; z-index: 1000; background: #000;";
      container.innerHTML = `<div id="${playerId}" style="width: 100%; height: 100%;"></div>`;
      document.body.appendChild(container);

      try {
        const player = new window.YT.Player(playerId, {
          height: "100%",
          width: "100%",
          videoId: videoId,
          playerVars: {
            autoplay: 1,
            loop: 1,
            playlist: videoId,
            controls: 0,
            disablekb: 1,
            fs: 0,
            modestbranding: 1,
            rel: 0,
            showinfo: 0,
            mute: isMuted ? 1 : 0,
            start: 0,
            enablejsapi: 1,
            playsinline: 1,
            origin: window.location.origin,
            // 일부공개 영상 임베드 재생을 위한 추가 설정
            iv_load_policy: 3, // 주석 제거
            cc_load_policy: 0, // 자막 숨김
          },
          events: {
            onReady: (event) => {
              try {
                // TV 환경 자동재생 강화 - 즉시 재생 시도
                if (isPlaying) {
                  event.target.playVideo();

                  // TV 환경을 위한 추가 재생 시도 (지연)
                  setTimeout(() => {
                    try {
                      if (
                        event.target.getPlayerState() !==
                        window.YT.PlayerState.PLAYING
                      ) {
                        event.target.playVideo();
                      }
                    } catch (retryError) {
                      // Silent error handling
                    }
                  }, 500);

                  // 한 번 더 재시도
                  setTimeout(() => {
                    try {
                      if (
                        event.target.getPlayerState() !==
                        window.YT.PlayerState.PLAYING
                      ) {
                        event.target.playVideo();
                      }
                    } catch (retryError) {
                      // Silent error handling
                    }
                  }, 1500);
                }
              } catch (error) {
                // Silent error handling
              }
            },
            onStateChange: (event) => {
              if (event.data === window.YT.PlayerState.ENDED) {
                try {
                  event.target.seekTo(0);
                  event.target.playVideo();
                } catch (error) {
                  // Silent error handling
                }
              } else if (event.data === window.YT.PlayerState.PAUSED) {
                // 예상치 못한 일시정지 시 재시도 (TV 환경 대응)
                if (isPlaying) {
                  setTimeout(() => {
                    try {
                      event.target.playVideo();
                    } catch (error) {
                      // Silent error handling
                    }
                  }, 100);
                }
              }
            },
            onError: (event) => {
              // 재생 오류 발생 시 재시도
              console.error("YouTube Player Error:", event.data);

              // 특정 오류 코드에 대한 처리
              // 2: 잘못된 매개변수 값
              // 5: HTML5 플레이어 오류
              // 100: 동영상을 찾을 수 없음
              // 101, 150: 임베드 재생 제한

              if (event.data === 101 || event.data === 150) {
                // 임베드 제한 오류 - 재시도 로직
                setTimeout(() => {
                  try {
                    event.target.playVideo();
                  } catch (retryError) {
                    // Silent error handling
                  }
                }, 1000);
              } else if (event.data === 5) {
                // HTML5 플레이어 오류 - 재로드 시도
                setTimeout(() => {
                  try {
                    event.target.loadVideoById(videoId);
                    event.target.playVideo();
                  } catch (retryError) {
                    // Silent error handling
                  }
                }, 1500);
              }
            },
          },
        });

        setContentYouTubePlayer(player);
      } catch (error) {
        // Silent error handling
      }
    },
    [isPlaying, contentYouTubePlayer]
  );

  // YouTube 배경음악 재생 완료 시 처리 (기존 로직 완전 유지)
  const handleYouTubeMusicEnd = useCallback(
    (player) => {
      if (!player || !isBgmEnabled) return;

      const elapsedCycleTime = (Date.now() - musicStartTimeRef.current) / 1000;

      // 배경음악이 짧으면 반복 재생
      if (elapsedCycleTime < backgroundMusicDuration) {
        try {
          player.seekTo(0);
          player.playVideo();
        } catch (error) {
          // Silent error handling
        }
      }
    },
    [isBgmEnabled, backgroundMusicDuration]
  );

  // 컨텐츠 데이터 변경 감지 (기존 로직 완전 유지)
  useEffect(() => {
    if (contents && contents.length > 0 && !initialLoadHandledRef.current) {
      setCurrentContentIndex(0);
      setIsInitialLoad(true);
      setRenderElements([
        {
          index: 0,
          isExiting: false,
          isEntering: false,
          key: `content-0-${Date.now()}`,
        },
      ]);

      setTimeout(() => {
        setIsInitialLoad(false);
        initialLoadHandledRef.current = true;
      }, 100);
    } else if (
      contents &&
      contents.length > 0 &&
      initialLoadHandledRef.current
    ) {
      setCurrentContentIndex(0);
      setIsInitialLoad(false);
      setRenderElements([
        {
          index: 0,
          isExiting: false,
          isEntering: false,
          key: `content-0-${Date.now()}`,
        },
      ]);
    }
  }, [contents]);

  // sessionAuthenticated 변경 시 강제 배경음악 재초기화
  useEffect(() => {
    if (
      sessionAuthenticated &&
      userInteracted &&
      isBgmEnabled &&
      music &&
      music.length > 0
    ) {
      // 강제 초기화를 위해 기존 상태 완전 리셋
      musicInitializingRef.current = false;
      lastMusicDataRef.current = null;

      // 기존 배경음악 정리
      cleanupBackgroundMusic();

      // 짧은 지연 후 재초기화 (React 상태 업데이트 보장)
      setTimeout(() => {
        // 배경음악 강제 초기화 실행
        const initializeBackgroundMusic = async () => {
          try {
            musicInitializingRef.current = true;

            const firstMusic = music[0];
            if (!firstMusic || !firstMusic.bgm) {
              musicInitializingRef.current = false;
              return;
            }

            const bgm = firstMusic.bgm;
            const volume = (firstMusic.volume || 50) / 100;

            // 음악 타입별 처리 (기존 로직 완전 유지)
            if (bgm.type === "upload" || bgm.type === "FILE") {
              const audioUrl = bgm.url;
              if (audioUrl) {
                const audioElement = new Audio(audioUrl);
                audioElement.loop = false; // 수동으로 반복 관리
                audioElement.volume = volume;

                audioElement.addEventListener("loadeddata", () => {
                  setIsMusicInitialized(true);
                });

                audioElement.addEventListener("timeupdate", () => {
                  setMusicCurrentTime(audioElement.currentTime);
                });

                audioElement.addEventListener("ended", () => {
                  handleMusicEnd(audioElement);
                });

                audioElement.addEventListener("error", (e) => {
                  // Silent error handling
                });

                setBackgroundMusicPlayer(audioElement);
                backgroundMusicRef.current = audioElement;

                // 음악 재생 시작
                if (isPlaying) {
                  audioElement.play().catch((error) => {
                    // Silent error handling
                  });
                  musicStartTimeRef.current = Date.now();
                }
              }
            } else if (bgm.type === "url" || bgm.type === "LINK") {
              const audioUrl = bgm.url;
              if (audioUrl) {
                const audioElement = new Audio(audioUrl);
                audioElement.loop = false; // 수동으로 반복 관리
                audioElement.volume = volume;

                audioElement.addEventListener("loadeddata", () => {
                  setIsMusicInitialized(true);
                });

                audioElement.addEventListener("timeupdate", () => {
                  setMusicCurrentTime(audioElement.currentTime);
                });

                audioElement.addEventListener("ended", () => {
                  handleMusicEnd(audioElement);
                });

                audioElement.addEventListener("error", (e) => {
                  // Silent error handling
                });

                setBackgroundMusicPlayer(audioElement);
                backgroundMusicRef.current = audioElement;

                // 음악 재생 시작
                if (isPlaying) {
                  audioElement.play().catch((error) => {
                    // Silent error handling
                  });
                  musicStartTimeRef.current = Date.now();
                }
              }
            } else if (bgm.type === "youtube" || bgm.type === "YOUTUBE") {
              // YouTube 배경음악 처리 (원래 로직 완전 유지)
              const videoId = extractYouTubeAudioId(bgm.url);
              if (videoId) {
                loadYouTubeAPI(() => {
                  createBackgroundYouTubePlayer(videoId, volume);
                  musicStartTimeRef.current = Date.now();
                });
              }
            }
          } catch (error) {
            // Silent error handling
          } finally {
            // 초기화 완료 플래그 해제 (약간의 지연)
            setTimeout(() => {
              musicInitializingRef.current = false;
            }, 500);
          }
        };

        initializeBackgroundMusic();
      }, 100);
    }
  }, [sessionAuthenticated]); // sessionAuthenticated 변경 시에만 실행

  // 수정된 배경음악 초기화 및 관리 (PIN 인증 완료 후에만 초기화)
  useEffect(() => {
    // PIN 인증이 필요한 경우 인증 완료 전까지는 배경음악 초기화 안함
    if (!userInteracted || !isBgmEnabled || !sessionAuthenticated) {
      return;
    }

    // 중복 초기화 방지: 이미 초기화 중이거나 같은 데이터라면 스킵
    if (musicInitializingRef.current) {
      return;
    }

    // 동일한 music 데이터인지 확인 (JSON 비교)
    const currentMusicData = JSON.stringify(music);
    if (lastMusicDataRef.current === currentMusicData) {
      return;
    }

    musicInitializingRef.current = true;
    lastMusicDataRef.current = currentMusicData;

    const initializeBackgroundMusic = async () => {
      try {
        // 기존 배경음악 완전 정리
        cleanupBackgroundMusic();

        const firstMusic = music[0];
        if (!firstMusic || !firstMusic.bgm) {
          musicInitializingRef.current = false;
          return;
        }

        const bgm = firstMusic.bgm;
        const volume = (firstMusic.volume || 50) / 100;

        // 음악 타입별 처리 (기존 로직 완전 유지)
        if (bgm.type === "upload" || bgm.type === "FILE") {
          const audioUrl = bgm.url;
          if (audioUrl) {
            const audioElement = new Audio(audioUrl);
            audioElement.loop = false; // 수동으로 반복 관리
            audioElement.volume = volume;

            audioElement.addEventListener("loadeddata", () => {
              setIsMusicInitialized(true);
            });

            audioElement.addEventListener("timeupdate", () => {
              setMusicCurrentTime(audioElement.currentTime);
            });

            audioElement.addEventListener("ended", () => {
              handleMusicEnd(audioElement);
            });

            audioElement.addEventListener("error", (e) => {
              // Silent error handling
            });

            setBackgroundMusicPlayer(audioElement);
            backgroundMusicRef.current = audioElement;

            // 음악 재생 시작
            if (isPlaying) {
              audioElement.play().catch((error) => {
                // Silent error handling
              });
              musicStartTimeRef.current = Date.now();
            }
          }
        } else if (bgm.type === "url" || bgm.type === "LINK") {
          const audioUrl = bgm.url;
          if (audioUrl) {
            const audioElement = new Audio(audioUrl);
            audioElement.loop = false; // 수동으로 반복 관리
            audioElement.volume = volume;

            audioElement.addEventListener("loadeddata", () => {
              setIsMusicInitialized(true);
            });

            audioElement.addEventListener("timeupdate", () => {
              setMusicCurrentTime(audioElement.currentTime);
            });

            audioElement.addEventListener("ended", () => {
              handleMusicEnd(audioElement);
            });

            audioElement.addEventListener("error", (e) => {
              // Silent error handling
            });

            setBackgroundMusicPlayer(audioElement);
            backgroundMusicRef.current = audioElement;

            // 음악 재생 시작
            if (isPlaying) {
              audioElement.play().catch((error) => {
                // Silent error handling
              });
              musicStartTimeRef.current = Date.now();
            }
          }
        } else if (bgm.type === "youtube" || bgm.type === "YOUTUBE") {
          // YouTube 배경음악 처리 (원래 로직 완전 유지)
          const videoId = extractYouTubeAudioId(bgm.url);
          if (videoId) {
            loadYouTubeAPI(() => {
              createBackgroundYouTubePlayer(videoId, volume);
              musicStartTimeRef.current = Date.now();
            });
          }
        }
      } catch (error) {
        // Silent error handling
      } finally {
        // 초기화 완료 플래그 해제 (약간의 지연)
        setTimeout(() => {
          musicInitializingRef.current = false;
        }, 500);
      }
    };

    initializeBackgroundMusic();

    // 컴포넌트 언마운트 시 정리 (기존 로직 + 컨텐츠 플레이어 추가)
    return () => {
      cleanupBackgroundMusic();

      // YouTube 컨텐츠 플레이어 정리 (추가)
      if (contentYouTubePlayer) {
        try {
          if (contentYouTubePlayer.pauseVideo) {
            contentYouTubePlayer.pauseVideo();
          }
          if (contentYouTubePlayer.destroy) {
            contentYouTubePlayer.destroy();
          }
        } catch (error) {
          // Silent error handling
        }
      }

      // YouTube 컨테이너들 정리
      const contentContainer = document.getElementById(
        "contentYouTubeContainer"
      );
      if (contentContainer) {
        contentContainer.remove();
      }

      musicInitializingRef.current = false;
    };
  }, [
    userInteracted,
    isBgmEnabled,
    sessionAuthenticated, // PIN 인증 완료 상태 의존성 추가
    music, // music 의존성 유지하되 중복 초기화 방지
    isPlaying,
    createBackgroundYouTubePlayer,
    cleanupBackgroundMusic,
  ]);

  // 배경음악 파일 재생 완료 시 처리 (기존 로직 완전 유지)
  const handleMusicEnd = useCallback(
    (audioElement) => {
      if (!audioElement || !isBgmEnabled) return;

      const elapsedCycleTime = (Date.now() - musicStartTimeRef.current) / 1000;

      // 배경음악 파일이 짧으면 반복 재생
      if (elapsedCycleTime < backgroundMusicDuration) {
        audioElement.currentTime = 0;
        audioElement.play().catch((error) => {
          // Silent error handling
        });
      }
    },
    [isBgmEnabled, backgroundMusicDuration]
  );

  // 컨텐츠별 배경음악 제어 (YouTube vs 일반 동영상 구분)
  useEffect(() => {
    if (!isBgmEnabled) return;

    // 현재 컨텐츠가 YouTube 동영상인지 확인 (전체 공개/일부 공개 모두 포함)
    const isYouTubeVideoContent =
      currentContent?.content?.type === "video" &&
      (currentContent?.content?.videoType === "youtube" ||
        (currentContent?.content?.url &&
          currentContent?.content?.url.includes("youtube"))) &&
      currentContent?.isMusicPlay === true;

    // 현재 컨텐츠가 일반 동영상 (non-YouTube)인지 확인
    const isGeneralVideoContent =
      currentContent?.content?.type === "video" &&
      currentContent?.content?.videoType !== "youtube" &&
      !currentContent?.content?.url?.includes("youtube") &&
      currentContent?.isMusicPlay === true;

    // YouTube 동영상인 경우 배경음악 완전 정지 (전체 공개/일부 공개 모두)
    if (isYouTubeVideoContent) {
      // 일반 오디오 배경음악 일시정지
      if (backgroundMusicPlayer && !backgroundMusicPlayer.paused) {
        backgroundMusicPlayer.pause();
      }

      // YouTube 배경음악 일시정지
      if (backgroundYouTubePlayer) {
        try {
          backgroundYouTubePlayer.pauseVideo();
        } catch (error) {
          // Silent error handling
        }
      }
    }
    // 일반 동영상인 경우 볼륨만 0으로 (시간은 계속 진행)
    else if (isGeneralVideoContent) {
      // 일반 오디오 배경음악 볼륨 0 (시간은 계속 진행)
      if (backgroundMusicPlayer) {
        backgroundMusicPlayer.volume = 0;
      }

      // YouTube 배경음악 볼륨 0 (시간은 계속 진행)
      if (backgroundYouTubePlayer) {
        try {
          backgroundYouTubePlayer.setVolume(0);
        } catch (error) {
          // Silent error handling
        }
      }
    }
    // 이미지나 웹사이트 등 기타 컨텐츠인 경우 볼륨 복구 및 재생 재개
    else if (currentContent?.content?.type !== "video") {
      // 일반 오디오 배경음악 볼륨 복구 및 재생
      if (backgroundMusicPlayer) {
        const originalVolume =
          music && music.length > 0 ? (music[0].volume || 50) / 100 : 0.5;
        backgroundMusicPlayer.volume = originalVolume;

        // 일시정지된 상태라면 재생 재개
        if (backgroundMusicPlayer.paused && isPlaying) {
          backgroundMusicPlayer.play().catch((error) => {
            // Silent error handling
          });
        }
      }

      // YouTube 배경음악 볼륨 복구 및 재생
      if (backgroundYouTubePlayer) {
        try {
          const originalVolume =
            music && music.length > 0 ? music[0].volume || 50 : 50;
          backgroundYouTubePlayer.setVolume(originalVolume);

          // 일시정지된 상태라면 재생 재개
          if (isPlaying) {
            backgroundYouTubePlayer.playVideo();
          }
        } catch (error) {
          // Silent error handling
        }
      }
    }
  }, [
    currentContent,
    backgroundMusicPlayer,
    backgroundYouTubePlayer,
    isBgmEnabled,
    music,
    isPlaying,
  ]);

  // 전체 사이클 타이머 (배경음악과 컨텐츠 동기화) - 기존 로직 완전 유지
  useEffect(() => {
    if (!isPlaying || !isBgmEnabled) return;

    // 사이클 타이머 시작
    const startTime = Date.now();
    musicStartTimeRef.current = startTime;

    const updateCycleTime = () => {
      const elapsed = (Date.now() - startTime) / 1000;
      setCurrentCycleTime(elapsed);

      // 배경음악 반복 로직
      if (backgroundMusicDuration > 0) {
        // Case 1: 배경음악 시간이 더 길면 컨텐츠 반복 시 배경음악도 처음부터 다시 시작
        if (backgroundMusicDuration > totalContentDuration) {
          if (elapsed >= totalContentDuration) {
            setCurrentCycleTime(0);
            musicStartTimeRef.current = Date.now();

            // 일반 오디오 플레이어 다시 시작
            if (backgroundMusicPlayer) {
              backgroundMusicPlayer.currentTime = 0;
              backgroundMusicPlayer.play().catch((error) => {
                // Silent error handling
              });
            }

            // YouTube 플레이어 다시 시작
            if (backgroundYouTubePlayer) {
              try {
                backgroundYouTubePlayer.seekTo(0);
                backgroundYouTubePlayer.playVideo();
              } catch (error) {
                // Silent error handling
              }
            }
          }
        }
        // Case 2: 배경음악 시간이 더 짧으면 배경음악 반복
        else {
          if (
            elapsed >= Math.max(totalContentDuration, backgroundMusicDuration)
          ) {
            setCurrentCycleTime(0);
            musicStartTimeRef.current = Date.now();

            // 일반 오디오 플레이어 다시 시작
            if (backgroundMusicPlayer) {
              backgroundMusicPlayer.currentTime = 0;
              backgroundMusicPlayer.play().catch((error) => {
                // Silent error handling
              });
            }

            // YouTube 플레이어 다시 시작
            if (backgroundYouTubePlayer) {
              try {
                backgroundYouTubePlayer.seekTo(0);
                backgroundYouTubePlayer.playVideo();
              } catch (error) {
                // Silent error handling
              }
            }
          }
        }
      }
    };

    const intervalId = setInterval(updateCycleTime, 100);

    return () => {
      clearInterval(intervalId);
    };
  }, [
    isPlaying,
    isBgmEnabled,
    backgroundMusicDuration,
    totalContentDuration,
    backgroundMusicPlayer,
    backgroundYouTubePlayer,
    cycleCount,
  ]);

  // 새로운 사이클 시작시 배경음악 재시작 (기존 로직 완전 유지)
  useEffect(() => {
    if (cycleCount > 0 && isBgmEnabled) {
      // 일반 오디오 플레이어 재시작
      if (backgroundMusicPlayer) {
        backgroundMusicPlayer.currentTime = 0;
        backgroundMusicPlayer.play().catch((error) => {
          // Silent error handling
        });
      }

      // YouTube 플레이어 재시작
      if (backgroundYouTubePlayer) {
        try {
          backgroundYouTubePlayer.seekTo(0);
          backgroundYouTubePlayer.playVideo();
        } catch (error) {
          // Silent error handling
        }
      }

      musicStartTimeRef.current = Date.now();
      setCurrentCycleTime(0);
    }
  }, [
    cycleCount,
    backgroundMusicPlayer,
    backgroundYouTubePlayer,
    isBgmEnabled,
  ]);

  // 키보드 제어 (재생/일시정지, 이전/다음) - 기존 로직 + 컨텐츠 플레이어 추가
  useEffect(() => {
    const handleKeyDown = (event) => {
      const keyCode = event.keyCode || event.which;

      if (keyCode === 32 || keyCode === 13) {
        // Space 또는 Enter
        event.preventDefault();
        setIsPlaying((prev) => {
          const newPlaying = !prev;

          // 일반 오디오 플레이어 제어
          if (backgroundMusicPlayer) {
            if (newPlaying) {
              backgroundMusicPlayer.play().catch((error) => {
                // Silent error handling
              });
            } else {
              backgroundMusicPlayer.pause();
            }
          }

          // YouTube 배경음악 플레이어 제어
          if (backgroundYouTubePlayer) {
            try {
              if (newPlaying) {
                backgroundYouTubePlayer.playVideo();
              } else {
                backgroundYouTubePlayer.pauseVideo();
              }
            } catch (error) {
              // Silent error handling
            }
          }

          // YouTube 컨텐츠 플레이어 제어 (추가)
          if (contentYouTubePlayer) {
            try {
              if (newPlaying) {
                contentYouTubePlayer.playVideo();
              } else {
                contentYouTubePlayer.pauseVideo();
              }
            } catch (error) {
              // Silent error handling
            }
          }

          return newPlaying;
        });
      } else if (keyCode === 37 || keyCode === 38) {
        // 왼쪽, 위쪽
        event.preventDefault();
        setCurrentContentIndex((prev) =>
          prev > 0 ? prev - 1 : contents.length - 1
        );
      } else if (keyCode === 39 || keyCode === 40) {
        // 오른쪽, 아래쪽
        event.preventDefault();
        setCurrentContentIndex((prev) => (prev + 1) % contents.length);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    contents.length,
    backgroundMusicPlayer,
    backgroundYouTubePlayer,
    contentYouTubePlayer,
  ]);

  // 컨텐츠 렌더링 함수 (YouTube는 신호만, 실제 플레이어는 별도 관리)
  const renderContent = (contentIndex, className = "", key = null) => {
    if (!contents || !contents[contentIndex]) return null;

    const content = contents[contentIndex];
    const contentData = content.content;

    if (!contentData) {
      return null;
    }

    const finalKey = key || `content-${contentIndex}-${cycleCount}`;

    switch (contentData.type) {
      case "image":
      case "IMAGE":
        return (
          <div
            key={finalKey}
            className={`content-item image-slide ${className}`}
            style={{
              backgroundImage: `url(${contentData.url})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}
          />
        );

      case "video":
      case "VIDEO":
        const videoUrl = contentData.url;
        const videoType = contentData.videoType;

        if (
          videoType === "youtube" ||
          (videoUrl && videoUrl.includes("youtube"))
        ) {
          // YouTube 컨텐츠: 공개 여부에 따라 다른 방식 사용
          const videoId = extractYouTubeVideoId(videoUrl);
          const isMuted = !content.isMusicPlay;
          const visibility = contentData.youtubeVisibility || "unlisted";

          // 전체 공개 영상: YouTube Player API 사용 (동적 플레이어 생성)
          if (visibility === "public") {
            return (
              <div
                key={finalKey}
                className={`content-item video-slide ${className}`}
                style={{
                  width: "100%",
                  height: "100%",
                  background: "#000",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  position: "relative",
                }}
              >
                {/* YouTube Player API가 동적으로 표시됨 */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "absolute",
                    zIndex: 5,
                  }}
                >
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      border: "4px solid rgba(255,255,255,0.3)",
                      borderTop: "4px solid #ffffff",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                      marginBottom: "16px",
                    }}
                  ></div>
                  <div
                    style={{
                      fontSize: "18px",
                      color: "white",
                      opacity: 0.8,
                      textAlign: "center",
                    }}
                  >
                    재생 준비중입니다.
                  </div>
                </div>
              </div>
            );
          }

          // 일부 공개 영상: iframe 직접 삽입 방식 (제한된 임베드)
          return (
            <div
              key={finalKey}
              className={`content-item video-slide ${className}`}
              style={{
                width: "100%",
                height: "100%",
                background: "#000",
                position: "relative",
              }}
            >
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&loop=1&playlist=${videoId}&controls=0&disablekb=1&fs=0&modestbranding=1&rel=0&showinfo=0&mute=${
                  isMuted ? 1 : 0
                }&enablejsapi=1&playsinline=1&iv_load_policy=3&cc_load_policy=0`}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
              />
            </div>
          );
        } else {
          // 일반 업로드된 비디오 (기존 로직 유지)
          const muteAttr =
            userInteracted && content.isMusicPlay === true
              ? {}
              : { muted: true };

          return (
            <div
              key={finalKey}
              className={`content-item video-slide ${className}`}
            >
              <video
                src={videoUrl}
                autoPlay
                loop
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                {...muteAttr}
              />
            </div>
          );
        }
        return null;

      case "website":
      case "WEBSITE":
        return (
          <div
            key={finalKey}
            className={`content-item website-slide ${className}`}
          >
            <iframe
              src={contentData.url}
              frameBorder="0"
              title={`Website: ${content.name || contentData.url}`}
              style={{ width: "100%", height: "100%" }}
              sandbox="allow-scripts allow-same-origin allow-forms"
            />
          </div>
        );

      default:
        return (
          <div
            key={finalKey}
            className={`content-item error-slide ${className}`}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                fontSize: "24px",
                color: "#666",
              }}
            >
              지원하지 않는 컨텐츠 타입입니다: {contentData.type}
            </div>
          </div>
        );
    }
  };

  // YouTube 배경음악 플레이어 임시 저장용
  const tempYouTubePlayerRef = useRef(null);

  // 전환 효과 처리 함수 (YouTube 플레이어 충돌 방지)
  const transitionToNext = useCallback(() => {
    if (isTransitioning || contents.length === 0) return;

    const nextIndex = (currentContentIndex + 1) % contents.length;
    const nextContent = contents[nextIndex];

    setIsTransitioning(true);
    setNextContentIndex(nextIndex);

    // 다음 컨텐츠가 YouTube인지 확인
    const nextIsYouTubeContent =
      nextContent?.content?.type === "video" &&
      (nextContent?.content?.videoType === "youtube" ||
        (nextContent?.content?.url &&
          nextContent?.content?.url.includes("youtube"))) &&
      nextContent?.isMusicPlay === true;

    // 다음 컨텐츠가 전체 공개 YouTube 영상인지 확인 (Player API 사용)
    const nextIsPublicYouTube =
      nextIsYouTubeContent &&
      (nextContent?.content?.youtubeVisibility || "unlisted") === "public";

    // 현재 컨텐츠가 YouTube인지 확인
    const currentIsYouTubeContent =
      currentContent?.content?.type === "video" &&
      (currentContent?.content?.videoType === "youtube" ||
        (currentContent?.content?.url &&
          currentContent?.content?.url.includes("youtube"))) &&
      currentContent?.isMusicPlay === true;

    // 현재 컨텐츠가 전체 공개 YouTube 영상인지 확인
    const currentIsPublicYouTube =
      currentIsYouTubeContent &&
      (currentContent?.content?.youtubeVisibility || "unlisted") === "public";

    // 배경음악도 YouTube인지 확인
    const hasYouTubeBgm =
      music &&
      music.length > 0 &&
      (music[0].bgm?.type === "youtube" || music[0].bgm?.type === "YOUTUBE");

    // 다른 컨텐츠 → 전체 공개 YouTube 컨텐츠: TV 환경 충돌 방지를 위한 완전 분리
    if (!currentIsPublicYouTube && nextIsPublicYouTube && isBgmEnabled) {
      // 일반 배경음악 정지
      if (backgroundMusicPlayer) {
        backgroundMusicPlayer.pause();
      }

      // YouTube 배경음악이 있으면 TV 환경을 위해 완전 제거 (일시적)
      if (backgroundYouTubePlayer && hasYouTubeBgm) {
        try {
          // 현재 재생 시간 저장
          const currentTime = backgroundYouTubePlayer.getCurrentTime
            ? backgroundYouTubePlayer.getCurrentTime()
            : 0;
          tempYouTubePlayerRef.current = {
            videoId: extractYouTubeAudioId(music[0].bgm.url),
            volume: music[0].volume || 50,
            currentTime: currentTime,
          };

          // 플레이어 완전 제거
          backgroundYouTubePlayer.pauseVideo();
          backgroundYouTubePlayer.stopVideo();
          backgroundYouTubePlayer.destroy();
          setBackgroundYouTubePlayer(null);

          // 컨테이너도 제거
          const bgContainer = document.getElementById(
            "backgroundYouTubeContainer"
          );
          if (bgContainer) {
            bgContainer.remove();
          }
        } catch (error) {
          // Silent error handling
        }
      }
      // YouTube 배경음악이 아니면 단순 정지
      else if (backgroundYouTubePlayer) {
        try {
          backgroundYouTubePlayer.pauseVideo();
        } catch (error) {
          // Silent error handling
        }
      }

      // 잠시 후 컨텐츠 플레이어 생성 (TV 환경 안정화) - 전체 공개 영상만
      const nextVideoId = extractYouTubeVideoId(nextContent.content.url);
      if (nextVideoId && window.YT) {
        setTimeout(
          () => {
            createContentYouTubePlayer(nextVideoId, !nextContent.isMusicPlay);
          },
          hasYouTubeBgm ? 300 : 100
        ); // YouTube BGM이 있으면 더 긴 지연
      }
    }

    // 전체 공개 YouTube 컨텐츠 → 다른 컨텐츠: 컨텐츠 플레이어 정리, 배경음악 재개 예약
    if (currentIsPublicYouTube && !nextIsPublicYouTube && isBgmEnabled) {
      // 컨텐츠 플레이어 즉시 정리 (개선된 로직)
      if (contentYouTubePlayer) {
        try {
          contentYouTubePlayer.pauseVideo();
          contentYouTubePlayer.stopVideo(); // 정지 추가
          // 즉시 제거하지 않고 약간의 지연 후 제거
          setTimeout(() => {
            try {
              contentYouTubePlayer.destroy();
            } catch (destroyError) {
              // Silent error handling
            }
            setContentYouTubePlayer(null);

            // 컨테이너 완전 정리
            const container = document.getElementById(
              "contentYouTubeContainer"
            );
            if (container) {
              container.innerHTML = "";
              container.remove();
            }
          }, 200);
        } catch (error) {
          // Silent error handling
        }
      } else {
        // 플레이어 객체가 없어도 컨테이너 정리
        const container = document.getElementById("contentYouTubeContainer");
        if (container) {
          container.innerHTML = "";
          container.remove();
        }
      }
    }

    // 전체 공개 YouTube → 전체 공개 YouTube: 기존 플레이어 정리 후 새 플레이어 생성
    if (currentIsPublicYouTube && nextIsPublicYouTube) {
      // 기존 플레이어 완전 정리
      if (contentYouTubePlayer) {
        try {
          contentYouTubePlayer.pauseVideo();
          contentYouTubePlayer.stopVideo();
          setTimeout(() => {
            try {
              contentYouTubePlayer.destroy();
            } catch (destroyError) {
              // Silent error handling
            }
            setContentYouTubePlayer(null);
          }, 100);
        } catch (error) {
          // Silent error handling
        }
      }

      // 새 플레이어 생성
      const nextVideoId = extractYouTubeVideoId(nextContent.content.url);
      if (nextVideoId && window.YT) {
        setTimeout(
          () => {
            createContentYouTubePlayer(nextVideoId, !nextContent.isMusicPlay);
          },
          hasYouTubeBgm ? 500 : 300
        ); // YouTube BGM이 있으면 더 긴 지연시간
      }
    }

    // 새로운 엘리먼트 추가 (entering 상태로)
    const newKey = `content-${nextIndex}-${Date.now()}`;
    setRenderElements((prev) => [
      ...prev.map((el) => ({ ...el, isExiting: true })), // 기존 엘리먼트들을 exiting 상태로
      { index: nextIndex, isExiting: false, isEntering: true, key: newKey },
    ]);

    // 짧은 지연 후 entering 클래스 제거 (CSS transition 시작)
    setTimeout(() => {
      setRenderElements((prev) =>
        prev.map((el) =>
          el.key === newKey ? { ...el, isEntering: false } : el
        )
      );
    }, 50);

    // 전환 완료 후 정리
    setTimeout(() => {
      setCurrentContentIndex(nextIndex);
      setRenderElements([
        { index: nextIndex, isExiting: false, isEntering: false, key: newKey },
      ]);
      setIsTransitioning(false);
      setNextContentIndex(null);

      // 전체 공개 YouTube 컨텐츠 → 다른 컨텐츠: 배경음악 재개 (딜레이)
      if (currentIsPublicYouTube && !nextIsPublicYouTube && isBgmEnabled) {
        setTimeout(() => {
          // 일반 배경음악 재개
          if (backgroundMusicPlayer && backgroundMusicPlayer.paused) {
            backgroundMusicPlayer.play().catch((error) => {
              // Silent error handling
            });
          }

          // YouTube 배경음악이 제거되었다면 다시 생성
          if (tempYouTubePlayerRef.current && hasYouTubeBgm) {
            const savedData = tempYouTubePlayerRef.current;
            tempYouTubePlayerRef.current = null;

            setTimeout(() => {
              createBackgroundYouTubePlayer(
                savedData.videoId,
                savedData.volume / 100
              );

              // 이전 재생 위치로 복구 시도
              setTimeout(() => {
                if (backgroundYouTubePlayer && savedData.currentTime > 0) {
                  try {
                    backgroundYouTubePlayer.seekTo(savedData.currentTime);
                  } catch (error) {
                    // Silent error handling
                  }
                }
              }, 1000);
            }, 200);
          }
          // YouTube 배경음악이 그냥 정지된 상태라면 재개
          else if (backgroundYouTubePlayer) {
            try {
              backgroundYouTubePlayer.playVideo(); // 실제 재생
            } catch (error) {
              // Silent error handling
            }
          }
        }, 500); // 재개 지연시간 단축
      }

      // 사이클 완료 체크
      if (nextIndex === 0) {
        if (onCycleComplete && typeof onCycleComplete === "function") {
          onCycleComplete();
        }
      }
    }, transitionDuration * 1000);
  }, [
    currentContentIndex,
    contents,
    isTransitioning,
    transitionDuration,
    onCycleComplete,
    currentContent,
    isBgmEnabled,
    backgroundMusicPlayer,
    backgroundYouTubePlayer,
    contentYouTubePlayer,
    createContentYouTubePlayer,
    music, // YouTube BGM 확인을 위해 추가
  ]);

  // 자동 순환 타이머 (기존 로직 완전 유지)
  useEffect(() => {
    if (!isPlaying || contents.length === 0 || isTransitioning) {
      return;
    }

    const currentDuration = (currentContent?.playDuration || 10) * 1000;

    contentTimerRef.current = setTimeout(() => {
      transitionToNext();
    }, currentDuration);

    return () => {
      if (contentTimerRef.current) {
        clearTimeout(contentTimerRef.current);
      }
    };
  }, [
    isPlaying,
    contents,
    currentContent,
    isTransitioning,
    transitionToNext,
    currentContentIndex,
  ]);

  // 첫 번째 전체 공개 YouTube 컨텐츠 플레이어 생성 (자동 재생 문제 해결)
  useEffect(() => {
    // 첫 번째 컨텐츠가 YouTube인지 확인
    const firstContent = contents[0];
    const isFirstContentYouTube =
      firstContent?.content?.type === "video" &&
      (firstContent?.content?.videoType === "youtube" ||
        (firstContent?.content?.url &&
          firstContent?.content?.url.includes("youtube"))) &&
      firstContent?.isMusicPlay === true;

    // 전체 공개 영상인지 확인
    const isFirstContentPublic =
      isFirstContentYouTube &&
      (firstContent?.content?.youtubeVisibility || "unlisted") === "public";

    // 첫 번째 컨텐츠가 전체 공개 YouTube 영상이고, 현재 첫 번째 컨텐츠를 보고 있으며, API가 로드되었고, 사용자 상호작용이 완료된 경우
    if (
      isFirstContentPublic &&
      currentContentIndex === 0 &&
      isYouTubeAPILoaded &&
      window.YT &&
      window.YT.Player &&
      userInteracted &&
      !contentYouTubePlayer
    ) {
      const firstVideoId = extractYouTubeVideoId(firstContent.content.url);
      if (firstVideoId) {
        // 배경음악과의 충돌 방지를 위한 지연 처리
        const hasYouTubeBgm =
          music &&
          music.length > 0 &&
          (music[0].bgm?.type === "youtube" ||
            music[0].bgm?.type === "YOUTUBE");

        const delay = hasYouTubeBgm ? 800 : 300; // YouTube BGM이 있으면 더 긴 지연

        setTimeout(() => {
          // 배경음악 일시 정지 (전체 공개 YouTube 컨텐츠이므로)
          if (isBgmEnabled) {
            if (backgroundMusicPlayer && !backgroundMusicPlayer.paused) {
              backgroundMusicPlayer.pause();
            }
            if (backgroundYouTubePlayer) {
              try {
                backgroundYouTubePlayer.pauseVideo();
              } catch (error) {
                // Silent error handling
              }
            }
          }

          // 첫 번째 전체 공개 YouTube 컨텐츠 플레이어 생성
          createContentYouTubePlayer(firstVideoId, !firstContent.isMusicPlay);
        }, delay);
      }
    }
  }, [
    contents,
    currentContentIndex,
    isYouTubeAPILoaded, // API 로드 상태 추가
    userInteracted,
    contentYouTubePlayer,
    music,
    isBgmEnabled,
    backgroundMusicPlayer,
    backgroundYouTubePlayer,
    createContentYouTubePlayer,
  ]);

  // YouTube API 로드 (YouTube가 있으면 로드)
  useEffect(() => {
    // YouTube 컨텐츠나 배경음악이 있는지 확인
    const hasYouTubeContent = contents.some((content) => {
      const videoUrl = content.content?.url;
      const videoType = content.content?.videoType;
      return (
        videoType === "youtube" || (videoUrl && videoUrl.includes("youtube"))
      );
    });

    const hasYouTubeMusic = music.some((musicItem) => {
      const bgm = musicItem.bgm;
      return bgm?.type === "youtube" || bgm?.type === "YOUTUBE";
    });

    // YouTube가 있고 API가 로드되지 않았으면 로드
    if ((hasYouTubeContent || hasYouTubeMusic) && !isYouTubeAPILoaded) {
      loadYouTubeAPI(() => {
        // YouTube API 로드 완료
      });
    }
  }, [contents, music, isYouTubeAPILoaded, loadYouTubeAPI]);

  return (
    <div
      className={`dashboard-content transition-${transitionStyle}`}
      style={{
        "--transition-duration": `${transitionDuration}s`,
      }}
    >
      {/* 모든 렌더링 엘리먼트 표시 */}
      {renderElements.map((element) => {
        let className = "";
        if (element.isExiting) className += " exiting";
        if (element.isEntering) className += " entering";

        return renderContent(element.index, className.trim(), element.key);
      })}

      {/* 숨겨진 배경음악 플레이어 (기존 로직 완전 유지) */}
      <audio
        id="backgroundMusicPlayer"
        style={{ display: "none" }}
        ref={backgroundMusicRef}
      />

      <style jsx>{`
        .dashboard-content {
          width: 100vw;
          height: 100vh;
          position: relative;
          overflow: hidden;
          background: #000;
        }

        .content-item {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          opacity: 1;
          transform: scale(1) translateX(0) translateY(0) rotateX(0) rotateY(0);
          transition: all var(--transition-duration) ease-in-out;
          z-index: 1;
        }

        .content-item.entering {
          z-index: 2;
        }

        .content-item.exiting {
          z-index: 0;
        }

        /* getIframe().classList용 hidden 클래스 */
        .hidden {
          display: none !important;
          opacity: 0 !important;
          visibility: hidden !important;
        }

        /* Fade 전환 */
        .transition-fade .content-item.exiting {
          opacity: 0;
        }

        .transition-fade .content-item.entering {
          opacity: 0;
        }

        /* Slide Left 전환 */
        .transition-slideLeft .content-item.exiting {
          transform: translateX(-100%);
        }

        .transition-slideLeft .content-item.entering {
          transform: translateX(100%);
        }

        /* Slide Right 전환 */
        .transition-slideRight .content-item.exiting {
          transform: translateX(100%);
        }

        .transition-slideRight .content-item.entering {
          transform: translateX(-100%);
        }

        /* Slide Up 전환 */
        .transition-slideUp .content-item.exiting {
          transform: translateY(-100%);
        }

        .transition-slideUp .content-item.entering {
          transform: translateY(100%);
        }

        /* Slide Down 전환 */
        .transition-slideDown .content-item.exiting {
          transform: translateY(100%);
        }

        .transition-slideDown .content-item.entering {
          transform: translateY(-100%);
        }

        /* Zoom In 전환 */
        .transition-zoomIn .content-item.exiting {
          transform: scale(1.5);
          opacity: 0;
        }

        .transition-zoomIn .content-item.entering {
          transform: scale(0.5);
          opacity: 0;
        }

        /* Zoom Out 전환 */
        .transition-zoomOut .content-item.exiting {
          transform: scale(0.5);
          opacity: 0;
        }

        .transition-zoomOut .content-item.entering {
          transform: scale(1.5);
          opacity: 0;
        }

        /* Rotate Left 전환 */
        .transition-rotateLeft .content-item.exiting {
          transform: rotateY(-90deg);
          opacity: 0;
        }

        .transition-rotateLeft .content-item.entering {
          transform: rotateY(90deg);
          opacity: 0;
        }

        /* Rotate Right 전환 */
        .transition-rotateRight .content-item.exiting {
          transform: rotateY(90deg);
          opacity: 0;
        }

        .transition-rotateRight .content-item.entering {
          transform: rotateY(-90deg);
          opacity: 0;
        }

        /* Flip Horizontal 전환 */
        .transition-flipHorizontal .content-item.exiting {
          transform: rotateY(90deg);
          opacity: 0;
        }

        .transition-flipHorizontal .content-item.entering {
          transform: rotateY(-90deg);
          opacity: 0;
        }

        /* Flip Vertical 전환 */
        .transition-flipVertical .content-item.exiting {
          transform: rotateX(90deg);
          opacity: 0;
        }

        .transition-flipVertical .content-item.entering {
          transform: rotateX(-90deg);
          opacity: 0;
        }

        /* Blinds 전환 */
        .transition-blinds .content-item.exiting {
          transform: scaleY(0);
          transform-origin: top;
        }

        .transition-blinds .content-item.entering {
          transform: scaleY(0);
          transform-origin: bottom;
        }

        /* Cube 전환 */
        .transition-cube .content-item.exiting {
          transform: rotateY(90deg) translateZ(-50vw);
          opacity: 0;
        }

        .transition-cube .content-item.entering {
          transform: rotateY(-90deg) translateZ(-50vw);
          opacity: 0;
        }

        /* 기타 스타일들 */
        .image-slide {
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
        }

        .video-slide,
        .website-slide {
          width: 100%;
          height: 100%;
        }

        .video-slide iframe,
        .video-slide video,
        .website-slide iframe {
          width: 100%;
          height: 100%;
          border: none;
        }

        .error-slide {
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f8f9fa;
          color: #666;
        }
      `}</style>
    </div>
  );
};

export default ContentDisplay;
